/**
 * The two public sites, one build:
 *
 *  - makeit.app (Firebase Hosting site apps-96adb): the whole web — landing,
 *    Home, editor, classrooms, subscription.
 *  - play.makeit.app (site makeitapp-play): only playing — a code box, the
 *    demos and the classroom student view. It opens the links the Unity app
 *    shares, `https://play.makeit.app?code=<code>` (HomeSharePanel.cs), and the
 *    old play site's `/play.html?code=` and `/mobile.php?code=` (firebase.json
 *    rewrites every path to index.html, keeping the query string). Its
 *    /libraries/** redirect to the main site, so they are deployed once.
 *
 * In development `?site=play` shows the play site.
 */

export const PLAY_ORIGIN = "https://play.makeit.app";
export const MAIN_ORIGIN = "https://makeit.app";

/** Hostnames of the play site: its domain and Firebase's default ones. */
const PLAY_HOSTS = new Set(["play.makeit.app", "makeitapp-play.web.app", "makeitapp-play.firebaseapp.com"]);

export const IS_PLAY_SITE: boolean =
  PLAY_HOSTS.has(location.hostname) || (import.meta.env.DEV && new URLSearchParams(location.search).get("site") === "play");

/** The link that plays a shared project, in the Unity app's format. */
export function playLink(code: string | number): string {
  return `${PLAY_ORIGIN}?code=${code}`;
}

/** Same, for a project without share code (played by id). */
export function playProjectLink(projectId: string): string {
  return `${PLAY_ORIGIN}?project=${encodeURIComponent(projectId)}`;
}

/** The audience pages of the main site (AudiencePage): Make It for schools / for special needs. */
export type Audience = "schools" | "special";
const AUDIENCE_PATHS: Record<Audience, string> = { schools: "/schools", special: "/special-needs" };

export function audiencePath(a: Audience): string {
  return AUDIENCE_PATHS[a];
}

/** The audience page at `pathname` (with or without a trailing slash), if any. */
export function audienceFor(pathname: string): Audience | null {
  const p = pathname.replace(/\/+$/, "");
  return (Object.keys(AUDIENCE_PATHS) as Audience[]).find((a) => AUDIENCE_PATHS[a] === p) ?? null;
}
